/**
 * AnalyticsPatientTable Component
 *
 * Interactive table showing patients that make up the analytics data
 * Updates dynamically based on chart interactions and drill-down filters
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  TablePagination,
} from '@mui/material';
import { PatientAnalyticsRecord } from '~/types/analytics';
import { WorkflowStage, PredictProgram } from '~/types/predict';
import { formatDistanceToNow } from 'date-fns';

export interface DrillDownFilter {
  type: 'stage' | 'program' | 'facility' | 'locationType' | 'backlog' | 'highPriority' | 'ready' | 'hasBlockers' | 'unassigned' | 'custom' | 'newCandidates' | 'myCases' | 'all-scored' | 'successful-discharges';
  label: string; // Display name for the filter
  stage?: WorkflowStage;
  program?: PredictProgram;
  facility?: string;
  locationType?: 'ED' | 'INPATIENT';
  dateRange?: { start: Date; end: Date };
  customFilter?: (patient: PatientAnalyticsRecord) => boolean;
}

interface AnalyticsPatientTableProps {
  patients: PatientAnalyticsRecord[];
  drillDownFilter: DrillDownFilter | null;
  onClearFilter: () => void;
}

const STAGE_LABELS: Record<WorkflowStage, string> = {
  [WorkflowStage.TRIAGE]: 'Triage',
  [WorkflowStage.ELIGIBILITY]: 'Eligibility',
  [WorkflowStage.ACCEPTANCE]: 'Acceptance',
  [WorkflowStage.TRANSITION]: 'Transition',
  [WorkflowStage.DISQUALIFIED]: 'Disqualified',
};

const PROGRAM_LABELS: Record<PredictProgram, string> = {
  [PredictProgram.HOME_HEALTH]: 'Home Health',
  [PredictProgram.HOSPITAL_AT_HOME]: 'Hospital at Home',
  [PredictProgram.SNF_AT_HOME]: 'SNF at Home',
  [PredictProgram.HOSPICE]: 'Hospice',
  [PredictProgram.INFUSION_AT_HOME]: 'Infusion at Home',
  [PredictProgram.PALLIATIVE]: 'Palliative',
};

const AnalyticsPatientTable: React.FC<AnalyticsPatientTableProps> = ({
  patients,
  drillDownFilter,
  onClearFilter,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter patients based on drill-down
  const filteredPatients = useMemo(() => {
    if (!drillDownFilter) return patients;

    return patients.filter(patient => {
      // If custom filter provided, use it
      if (drillDownFilter.customFilter) {
        return drillDownFilter.customFilter(patient);
      }

      // Otherwise use standard filters
      if (drillDownFilter.stage && patient.currentStage !== drillDownFilter.stage) {
        return false;
      }
      if (drillDownFilter.program) {
        // Match by actual discharge program (selectedProgram takes precedence over recommendedProgram)
        const actualProgram = patient.selectedProgram || patient.recommendedProgram;
        if (actualProgram !== drillDownFilter.program) {
          return false;
        }
      }
      if (drillDownFilter.facility && patient.facilityName !== drillDownFilter.facility) {
        return false;
      }
      if (drillDownFilter.locationType && patient.locationType !== drillDownFilter.locationType) {
        return false;
      }
      if (drillDownFilter.dateRange) {
        const admissionDate = new Date(patient.admissionTimestamp);
        if (admissionDate < drillDownFilter.dateRange.start ||
            admissionDate > drillDownFilter.dateRange.end) {
          return false;
        }
      }
      return true;
    });
  }, [patients, drillDownFilter]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedPatients = filteredPatients.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ mt: 4 }}>
      {/* Header with title and filter indicator */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Patient Details
          </Typography>
          <Chip
            label={`${filteredPatients.length} patient${filteredPatients.length !== 1 ? 's' : ''}`}
            size="small"
            color="primary"
            sx={{ fontWeight: 600 }}
          />
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          overflowX: 'auto',
          mb: 0,
        }}
      >
        <Table sx={{ width: '100%', tableLayout: 'fixed' }} stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: 'white',
              }}
            >
              <TableCell sx={{ fontWeight: 600, width: 150 }}>Patient Name</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 100 }}>MRN</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 180 }}>Facility</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 90 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 110 }}>Stage</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 160 }}>Recommended Program</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 160 }}>Selected Program</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 80 }}>Score</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 140 }}>Owner</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 120 }}>Time in System</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 100 }}>Blockers</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedPatients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No patients match the current filters
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedPatients.map((patient) => (
                <TableRow
                  key={patient.patientId}
                  hover
                  sx={{
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {patient.patientName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                      {patient.patientId.slice(-6).toUpperCase()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {patient.facilityName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {patient.locationType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {STAGE_LABELS[patient.currentStage]}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {PROGRAM_LABELS[patient.recommendedProgram]}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {patient.selectedProgram ? PROGRAM_LABELS[patient.selectedProgram] : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: patient.programScore >= 80 ? 'success.main' :
                               patient.programScore >= 60 ? 'warning.main' : 'error.main',
                      }}
                    >
                      {patient.programScore}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {patient.ownerName || 'Unassigned'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDistanceToNow(new Date(patient.admissionTimestamp), { addSuffix: false })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {patient.blockers.length > 0 ? (
                      <Chip
                        label={patient.blockers.length}
                        size="small"
                        color="warning"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Paper sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredPatients.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default AnalyticsPatientTable;
